<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { vOnClickOutside } from '@vueuse/components';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const topics = useMapGetter('topics/getTopics');

const isOpen = ref(false);
const isUpdating = ref(false);
const query = ref('');

const topicLabel = computed(() => props.conversation.topic_name || 'Add Topic');

const filteredTopics = computed(() => {
  const search = query.value.trim().toLowerCase();
  if (!search) return topics.value;
  return topics.value.filter(topic =>
    topic.name.toLowerCase().includes(search)
  );
});

const exactMatchExists = computed(() =>
  topics.value.some(
    topic => topic.name.toLowerCase() === query.value.trim().toLowerCase()
  )
);

const canCreate = computed(() => query.value.trim() && !exactMatchExists.value);

const closeAll = () => {
  isOpen.value = false;
  query.value = '';
};

const selectTopic = async topicId => {
  isUpdating.value = true;
  try {
    await store.dispatch('updateTopic', {
      conversationId: props.conversation.id,
      topicId,
    });
    closeAll();
  } catch (error) {
    useAlert('Unable to update topic.');
  } finally {
    isUpdating.value = false;
  }
};

const createAndSelectTopic = async () => {
  const name = query.value.trim();
  if (!name) return;
  isUpdating.value = true;
  try {
    const topic = await store.dispatch('topics/create', { name });
    await store.dispatch('updateTopic', {
      conversationId: props.conversation.id,
      topicId: topic.id,
    });
    closeAll();
  } catch (error) {
    useAlert('Unable to create topic.');
  } finally {
    isUpdating.value = false;
  }
};

const clearTopic = () => selectTopic(null);
</script>

<template>
  <div v-on-click-outside="closeAll" class="relative">
    <button
      type="button"
      class="flex items-center gap-1 h-8 px-2.5 rounded-md bg-n-alpha-2 text-xs font-medium text-n-slate-12 max-w-[9.5rem] sm:max-w-[12rem]"
      @click="isOpen = !isOpen"
    >
      <span class="truncate">{{ topicLabel }}</span>
      <span class="i-lucide-chevron-down size-3 flex-shrink-0" />
    </button>

    <div
      v-if="isOpen"
      class="absolute z-50 mt-1 top-full w-64 max-w-[calc(100vw-2rem)] left-0 xl:left-auto xl:ltr:right-0 xl:rtl:left-0 bg-n-alpha-3 backdrop-blur-[100px] border-0 outline outline-1 outline-n-container rounded-xl shadow-lg p-2 flex flex-col gap-1"
    >
      <input
        v-model="query"
        type="text"
        placeholder="Search or create a topic"
        class="reset-base w-full h-8 text-sm rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 px-2"
        :disabled="isUpdating"
      />
      <div class="max-h-48 overflow-y-auto flex flex-col">
        <button
          v-if="conversation.topic_id"
          type="button"
          class="text-left text-xs text-n-slate-11 px-2 py-1.5 rounded-md hover:bg-n-alpha-1"
          :disabled="isUpdating"
          @click="clearTopic"
        >
          Remove topic
        </button>
        <button
          v-for="topic in filteredTopics"
          :key="topic.id"
          type="button"
          class="text-left text-xs px-2 py-1.5 rounded-md hover:bg-n-alpha-1"
          :class="
            topic.id === conversation.topic_id
              ? 'text-n-blue-11 font-medium'
              : 'text-n-slate-12'
          "
          :disabled="isUpdating"
          @click="selectTopic(topic.id)"
        >
          {{ topic.name }}
        </button>
        <button
          v-if="canCreate"
          type="button"
          class="text-left text-xs text-n-blue-11 font-medium px-2 py-1.5 rounded-md hover:bg-n-alpha-1"
          :disabled="isUpdating"
          @click="createAndSelectTopic"
        >
          + Create "{{ query.trim() }}"
        </button>
      </div>
    </div>
  </div>
</template>
